import { Controller } from '@hotwired/stimulus';

// Some of many (`f.input as: :multiselect`, fire/docs/DESIGN.md § CRUD,
// docs/HOTWIRE.md): the select multiple stays the field that posts, hidden;
// this draws the box she types in and points the field's <label for> at it.
//
// The words AND the classes come from fire's form builder: no host's
// Tailwind scans fire's controllers, and every host scans fire's helpers.
export default class extends Controller {
  static targets = ['select'];
  // ui.create, ui.search, ui.select, fire.form.remove ("Remover %{name}").
  static values = { placeholder: String, create: String, search: String, remove: String };
  static classes = ['box', 'error', 'search', 'list', 'option', 'check', 'create', 'tag', 'untag'];

  connect() {
    this.initializeSelect();
  }

  disconnect() {
    document.removeEventListener('click', this.outside);
    // The label goes back to the select it was drawn for.
    if (this.label) this.label.htmlFor = this.originalSelect.id;

    // Remove the custom multiselect container
    if (this.container) {
      this.container.remove();
    }

    // Restore the original select element's visibility
    if (this.originalSelect) {
      this.originalSelect.style.display = '';
    }

    // Clear any references to avoid memory leaks
    this.container = null;
    this.displayArea = null;
    this.searchInput = null;
    this.dropdown = null;
    this.selectedOptions = null;
  }

  initializeSelect() {
    this.originalSelect = this.selectTarget;
    this.selectedOptions = new Set();
    this.createCustomMultiselect();
    this.populateInitialSelections();
  }

  createCustomMultiselect() {
    // Create the main container
    this.container = document.createElement('div');
    this.container.classList.add('relative', 'w-full');

    // Create the display area (like a select input)
    this.displayArea = document.createElement('div');
    this.displayArea.classList.add(...this.boxClasses);
    if (this.selectTarget.getAttribute('aria-invalid') === 'true') this.displayArea.classList.add(...this.errorClasses);
    this.displayArea.addEventListener('click', () => this.focusSearchInput());

    // Create search input
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.classList.add(...this.searchClasses);
    this.searchInput.placeholder = this.placeholderValue;
    // The select is hidden, so this box is the field: the <label> names it
    // (its `for` moves here), and it says the hint and the error fire's
    // builder named on the select (DESIGN.md § input).
    this.searchInput.id = `${this.selectTarget.id}_search`;
    this.label = this.selectTarget.labels[0];
    if (this.label) this.label.htmlFor = this.searchInput.id;
    ['aria-describedby', 'aria-invalid'].forEach((a) => {
      const v = this.selectTarget.getAttribute(a);
      if (v) this.searchInput.setAttribute(a, v);
    });
    this.searchInput.addEventListener('input', () => this.filterOptions());
    this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.searchInput.addEventListener('focus', () => this.openDropdown());

    // Create placeholder or selected items display
    this.updateDisplayArea();

    // Create dropdown
    this.dropdown = document.createElement('div');
    this.dropdown.classList.add(...this.listClasses, 'hidden');

    // Create options
    this.createOptions();

    // Append elements
    this.container.appendChild(this.displayArea);
    this.container.appendChild(this.dropdown);
    this.originalSelect.parentNode.insertBefore(this.container, this.originalSelect);
    this.originalSelect.style.display = 'none';

    // Close dropdown when clicking outside. Kept, so disconnect can take it
    // back: left on the document, a select a turbo-stream replaced threw on
    // every later click (its container is null by then).
    this.outside = (e) => {
      if (!this.container.contains(e.target)) {
        this.closeDropdown();
      }
    };
    document.addEventListener('click', this.outside);

    // The list is the box's: it shuts when focus leaves the box (Tab past its
    // last option, a click elsewhere) and on Escape, so it never sits over
    // what comes next, a modal's Cancel and Save (2.4.11). A press on the box
    // or its list keeps focus where it is: left to itself the input would
    // blur before the click lands and shut the list under the pointer.
    this.container.addEventListener('mousedown', (e) => {
      if (e.target !== this.searchInput) e.preventDefault();
    });
    // Focus going to something else shuts the list at once. Going to nothing
    // waits to see where it lands: a redrawn option hands focus to the one
    // that replaces it, and that is not leaving.
    this.container.addEventListener('focusout', (e) => {
      if (e.relatedTarget) {
        if (!this.container.contains(e.relatedTarget)) this.closeDropdown();
      } else {
        setTimeout(() => {
          if (this.container && !this.container.contains(document.activeElement)) this.closeDropdown();
        });
      }
    });
    this.container.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || this.dropdown.classList.contains('hidden')) return;
      e.preventDefault(); // a modal around the box stays: Escape shuts the list first
      e.stopPropagation();
      this.searchInput.focus(); // from an option, back to the box (it opens its list on focus)
      this.closeDropdown();
    });
  }

  createOptions() {
    this.dropdown.innerHTML = ''; // Clear existing options

    const searchTerm = this.searchInput.value.toLowerCase();
    let hasVisibleOptions = false;

    Array.from(this.originalSelect.options).forEach((option) => {
      if (option.value === '') return; // Skip empty options

      const optionText = option.textContent.toLowerCase();

      // Filter options based on search term
      if (searchTerm && !optionText.includes(searchTerm)) {
        return;
      }

      hasVisibleOptions = true;

      const optionElement = document.createElement('div');
      optionElement.classList.add(...this.optionClasses);
      optionElement.dataset.value = option.value;

      const label = document.createElement('span');
      label.textContent = option.textContent;

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.classList.add(...this.checkClasses);
      checkbox.checked = this.selectedOptions.has(option.value);
      checkbox.setAttribute('aria-label', option.textContent); // the span beside it is not its label (4.1.2)

      optionElement.appendChild(label);
      optionElement.appendChild(checkbox);

      optionElement.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleOption(option.value, option.textContent);
      });

      this.dropdown.appendChild(optionElement);
    });

    // Add "Create new option" if search term doesn't match any existing option
    if (searchTerm && !hasVisibleOptions) {
      this.createNewOptionElement(this.searchInput.value);
    }
  }

  createNewOptionElement(value) {
    const newOptionElement = document.createElement('div');
    newOptionElement.classList.add(...this.optionClasses, ...this.createClasses);

    const label = document.createElement('span');
    // What the person typed is text, never markup.
    const typed = document.createElement('strong');
    typed.textContent = value;
    label.append(`${this.createValue} "`, typed, '"');

    const icon = document.createElement('span');
    icon.textContent = '+';
    icon.setAttribute('aria-hidden', 'true');

    newOptionElement.appendChild(label);
    newOptionElement.appendChild(icon);

    newOptionElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.createAndSelectNewOption(value);
    });

    this.dropdown.appendChild(newOptionElement);
  }

  createAndSelectNewOption(value) {
    // Create new option in the original select
    const newOption = document.createElement('option');
    newOption.value = value;
    newOption.textContent = value;
    newOption.selected = true;
    this.originalSelect.appendChild(newOption);

    // Add to selected options
    this.selectedOptions.add(value);

    // Clear search input
    this.searchInput.value = '';

    // Update display and close dropdown
    this.updateDisplayArea();
    this.closeDropdown();
    this.dispatchChangeEvent();
  }

  handleKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = this.searchInput.value.trim();

      if (searchTerm) {
        // Check if the option already exists
        const existingOption = Array.from(this.originalSelect.options).find(
          (option) => option.textContent.toLowerCase() === searchTerm.toLowerCase(),
        );

        if (existingOption) {
          // Select existing option
          this.toggleOption(existingOption.value, existingOption.textContent);
        } else {
          // Create new option
          this.createAndSelectNewOption(searchTerm);
        }
      }
    } else if (e.key === 'Backspace' && this.searchInput.value === '') {
      // Remove last selected option when backspace is pressed on empty input
      const lastSelected = Array.from(this.selectedOptions).pop();
      if (lastSelected) {
        const option = this.optionFor(lastSelected);
        if (option) {
          this.toggleOption(lastSelected, option.textContent);
        }
      }
    }
  }

  filterOptions() {
    this.createOptions();
    this.openDropdown();
  }

  focusSearchInput() {
    this.searchInput.focus();
    this.openDropdown();
  }

  toggleOption(value, text) {
    const optionElement = this.rowFor(value);
    const checkbox = optionElement?.querySelector('input[type="checkbox"]');

    if (this.selectedOptions.has(value)) {
      this.selectedOptions.delete(value);
      if (checkbox) checkbox.checked = false;
      this.optionFor(value).selected = false;
    } else {
      this.selectedOptions.add(value);
      if (checkbox) checkbox.checked = true;
      this.optionFor(value).selected = true;
    }

    this.updateDisplayArea();
    this.dispatchChangeEvent();

    // Clear search input after selection. The options are drawn again, and an
    // option that held focus (Space on its checkbox) hands it to its new one.
    const held = this.dropdown.contains(document.activeElement);
    this.searchInput.value = '';
    this.createOptions();
    if (held) this.rowFor(value)?.querySelector('input')?.focus();
  }

  updateDisplayArea() {
    // Redraw the tags only: the box stays where it is, so it keeps focus
    // (taken out and put back it loses it, and the list shuts with it).
    this.displayArea.querySelectorAll('.badge').forEach((badge) => badge.remove());
    // The search input always sits at the end
    if (!this.displayArea.contains(this.searchInput)) this.displayArea.appendChild(this.searchInput);

    // Add selected items as tags
    this.selectedOptions.forEach((value) => {
      const option = this.optionFor(value);
      if (option) {
        const tag = this.createTag(value, option.textContent);
        this.searchInput.before(tag);
      }
    });

    // Update placeholder visibility
    if (this.selectedOptions.size === 0) {
      this.searchInput.placeholder = this.placeholderValue;
    } else {
      this.searchInput.placeholder = this.searchValue;
    }
  }

  createTag(value, text) {
    const tag = document.createElement('div');
    tag.classList.add(...this.tagClasses);

    const span = document.createElement('span');
    span.textContent = text;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.classList.add(...this.untagClasses);
    removeBtn.textContent = '×';
    // A bare × reads "times": the button says what it takes away.
    removeBtn.setAttribute('aria-label', this.removeValue.replace('%{name}', text));
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleOption(value, text);
    });

    tag.appendChild(span);
    tag.appendChild(removeBtn);

    return tag;
  }

  openDropdown() {
    this.dropdown.classList.remove('hidden');
    this.createOptions(); // Refresh options when opening
  }

  closeDropdown() {
    this.dropdown.classList.add('hidden');
    this.searchInput.value = ''; // Clear search when closing
  }

  populateInitialSelections() {
    // Check for pre-selected options
    Array.from(this.originalSelect.selectedOptions).forEach((option) => {
      if (option.value) {
        this.selectedOptions.add(option.value);
      }
    });
    this.updateDisplayArea();
    this.updateDropdownCheckboxes();
  }

  dispatchChangeEvent() {
    // Dispatch a change event on the original select
    const event = new Event('change', { bubbles: true });
    this.originalSelect.dispatchEvent(event);
  }

  // Method to programmatically set values
  setValue(values) {
    this.selectedOptions.clear();

    // Deselect all options first
    Array.from(this.originalSelect.options).forEach((option) => {
      option.selected = false;
    });

    // Select specified values
    values.forEach((value) => {
      this.selectedOptions.add(value);
      const option = this.optionFor(value);
      if (option) {
        option.selected = true;
      }
    });

    this.updateDisplayArea();
    this.updateDropdownCheckboxes();
    this.dispatchChangeEvent();
  }

  updateDropdownCheckboxes() {
    Array.from(this.dropdown.children).forEach((optionElement) => {
      const value = optionElement.dataset.value;
      const checkbox = optionElement.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = this.selectedOptions.has(value);
      }
    });
  }

  // A value is any text (Tubo 5", a tag): compared, never put in a selector.
  optionFor(value) {
    return Array.from(this.originalSelect.options).find((o) => o.value === value);
  }

  rowFor(value) {
    return Array.from(this.dropdown.children).find((r) => r.dataset.value === value);
  }

  // Getter for selected values
  get value() {
    return Array.from(this.selectedOptions);
  }
}
